/**
 * Threshold Explorer — pure teaching math.
 *
 * No DOM, no imports, no patient data. Every function here is unit-tested in
 * tests/unit/thresholdExplorer.node.spec.mjs.
 *
 * The classification implements the ESC 0/1-hour hs-cTnT algorithm (2023 ESC
 * ACS guideline, Supplementary Table S4, hs-cTnT Elecsys row):
 *   rule-out: 0h < 5 ng/L with onset > 3h before sampling, OR
 *             0h < 12 ng/L with a 1h change < 3 ng/L
 *   rule-in:  0h >= 52 ng/L, OR 1h change >= 5 ng/L
 *   observe:  neither criterion met
 * The direct very-low branch is evaluated first: a 0h value below 5 ng/L with
 * onset more than 3 hours before sampling rules out on the 0h sample alone.
 */

export const DEFAULT_CUTOFFS = Object.freeze({
  veryLow: 5,
  low: 12,
  noDelta: 3,
  high: 52,
  ruleInDelta: 5,
});

export const ZONES = Object.freeze(["rule-out", "observe", "rule-in"]);

/**
 * Enforce strict ordering between the movable threshold lines.
 * The sliders permit ruleOutLine >= ruleInLine (the JSON controls allow
 * rule-out up to 30 while rule-in reaches down to 20); in that state the
 * plotted observe band collapses while classification evaluates rule-out
 * first, so bands and counts disagree. Clamping the rule-out line one step
 * below the rule-in line keeps bands, dots, and counts consistent. Valid
 * for the shipped controls because the rule-in minimum (20) exceeds the
 * rule-out minimum (5) by more than one step.
 */
export function orderThresholdLines(ruleOutLine, ruleInLine, step = 1) {
  if (ruleOutLine < ruleInLine) return { ruleOutLine, ruleInLine };
  return { ruleOutLine: ruleInLine - step, ruleInLine };
}

/**
 * Classify one teaching patient by (0h value, 1h change).
 * @param {number} value0h 0-hour hs-cTnT in ng/L
 * @param {number} delta 1-hour absolute change in ng/L
 * @param {object} [options]
 * @param {number} [options.ruleOutLine=12] movable rule-out line (the "low" cutoff)
 * @param {number} [options.ruleInLine=52] movable rule-in line (the "high" cutoff)
 * @param {object} [options.cutoffs=DEFAULT_CUTOFFS] fixed Table S4 constants
 * @param {boolean} [options.onsetOver3h=true] chest-pain onset > 3h before 0h sample
 * @returns {"rule-out" | "observe" | "rule-in"}
 */
export function classifyPatient(
  value0h,
  delta,
  {
    ruleOutLine = DEFAULT_CUTOFFS.low,
    ruleInLine = DEFAULT_CUTOFFS.high,
    cutoffs = DEFAULT_CUTOFFS,
    onsetOver3h = true,
  } = {},
) {
  if (value0h < cutoffs.veryLow && onsetOver3h) return "rule-out";
  if (value0h < ruleOutLine && delta < cutoffs.noDelta) return "rule-out";
  if (value0h >= ruleInLine || delta >= cutoffs.ruleInDelta) return "rule-in";
  return "observe";
}

/**
 * Negative predictive value from sensitivity/specificity/prevalence (Bayes).
 * Returns null only when the denominator is zero (degenerate inputs).
 */
export function bayesNpv(sensitivity, specificity, prevalence) {
  const denom =
    specificity * (1 - prevalence) + (1 - sensitivity) * prevalence;
  if (denom === 0) return null;
  return (specificity * (1 - prevalence)) / denom;
}

/** Positive predictive value from sensitivity/specificity/prevalence (Bayes). */
export function bayesPpv(sensitivity, specificity, prevalence) {
  const denom =
    sensitivity * prevalence + (1 - specificity) * (1 - prevalence);
  if (denom === 0) return null;
  return (sensitivity * prevalence) / denom;
}

/** Deterministic seeded PRNG (mulberry32) so the illustration is reproducible. */
export function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function intInRange(rand, min, max) {
  return min + Math.floor(rand() * (max - min + 1));
}

/**
 * Generate the teaching cohort: a deterministic dot population calibrated so
 * that, at the verified Table S4 cutoffs and 17% prevalence, classification
 * reproduces the APACE+BACC zone rates (rule-out 57%, observe ~25%,
 * rule-in 18%) and the zone NSTEMI counts implied by the published
 * NPV (99.8%) and PPV (74.5%).
 *
 * Bucket plan for size 200 (34 NSTEMI = 17%):
 *   rule-out 114 dots, 0 NSTEMI  (112 standard + 2 direct-only: 0h 3–4, Δ 6–8,
 *                                  non-NSTEMI — these depend on the >3h onset
 *                                  condition and move to rule-in without it)
 *   observe   50 dots, 7 NSTEMI
 *   rule-in   36 dots, 27 NSTEMI
 * Zone membership is structural (counts are exact by construction); only the
 * within-bucket values and the display order are drawn from the seed.
 */
export function generateCohort({
  size = 200,
  prevalence = 0.17,
  seed = 20261024,
} = {}) {
  const rand = seededRandom(seed);
  const nstemiTotal = Math.round(size * prevalence);
  const ruleOutTotal = Math.round(size * 0.57);
  const ruleInTotal = Math.round(size * 0.18);
  const observeTotal = size - ruleOutTotal - ruleInTotal;
  // NSTEMI allocation implied by published NPV/PPV at 17% prevalence:
  // ruled-out NSTEMI ≈ 34 × (1 − 0.9933) ≈ 0; ruled-in NSTEMI ≈ 36 × 0.745 ≈ 27.
  const ruleInNstemi = Math.min(ruleInTotal, Math.round(ruleInTotal * 0.745));
  const ruleOutNstemi = 0;
  const observeNstemi = nstemiTotal - ruleInNstemi - ruleOutNstemi;

  const dots = [];
  const push = (zone, isNstemi, value0h, delta) => {
    dots.push({ id: dots.length, zone, isNstemi, value0h, delta });
  };
  // Rule-out standard: 0h below the low cutoff, small change.
  for (let i = 0; i < ruleOutTotal - 2; i++) {
    push("rule-out", false, intInRange(rand, 2, 11), intInRange(rand, 0, 2));
  }
  // Rule-out direct-only: very low 0h but a large change — rule-out holds only
  // via the >3h direct branch; without it these two dots rule in.
  for (let i = 0; i < 2; i++) {
    push("rule-out", false, intInRange(rand, 3, 4), intInRange(rand, 6, 8));
  }
  // Observe: mid-range 0h, change below the rule-in delta.
  for (let i = 0; i < observeTotal; i++) {
    push(
      "observe",
      i < observeNstemi,
      intInRange(rand, 12, 51),
      intInRange(rand, 0, 4),
    );
  }
  // Rule-in: high 0h (change mixed — high value alone rules in).
  for (let i = 0; i < ruleInTotal; i++) {
    push(
      "rule-in",
      i < ruleInNstemi,
      intInRange(rand, 52, 120),
      intInRange(rand, 0, 12),
    );
  }
  // Deterministic shuffle so zones are interleaved on the axis.
  for (let i = dots.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [dots[i], dots[j]] = [dots[j], dots[i]];
  }
  dots.forEach((dot, index) => {
    dot.id = index;
  });
  return dots;
}

/**
 * Classify a whole cohort under the given controls.
 * @param {Array} dots output of generateCohort
 * @param {object} [options]
 * @param {"mixed" | "small" | "large"} [options.deltaMode="mixed"]
 *   mixed: each dot's own generated change;
 *   small: every dot's change treated as < 3 ng/L;
 *   large: every dot's change treated as >= 5 ng/L (teaching simplification:
 *          in real cohorts only a minority show a large rise).
 * @returns {{counts: object, npv: number|null, ppv: number|null}}
 *   counts: { "rule-out": { total, nstemi }, observe: {...}, "rule-in": {...} }
 *   npv/ppv: illustration values computed from the dots (null when the zone
 *   is empty); the published APACE values are shown separately in the UI.
 */
export function classifyCohort(
  dots,
  {
    ruleOutLine = DEFAULT_CUTOFFS.low,
    ruleInLine = DEFAULT_CUTOFFS.high,
    cutoffs = DEFAULT_CUTOFFS,
    onsetOver3h = true,
    deltaMode = "mixed",
  } = {},
) {
  const counts = {
    "rule-out": { total: 0, nstemi: 0 },
    observe: { total: 0, nstemi: 0 },
    "rule-in": { total: 0, nstemi: 0 },
  };
  for (const dot of dots) {
    const delta =
      deltaMode === "small"
        ? cutoffs.noDelta - 1
        : deltaMode === "large"
          ? cutoffs.ruleInDelta + 1
          : dot.delta;
    const zone = classifyPatient(dot.value0h, delta, {
      ruleOutLine,
      ruleInLine,
      cutoffs,
      onsetOver3h,
    });
    counts[zone].total += 1;
    if (dot.isNstemi) counts[zone].nstemi += 1;
  }
  const ruleOut = counts["rule-out"];
  const ruleIn = counts["rule-in"];
  return {
    counts,
    npv:
      ruleOut.total === 0
        ? null
        : (ruleOut.total - ruleOut.nstemi) / ruleOut.total,
    ppv: ruleIn.total === 0 ? null : ruleIn.nstemi / ruleIn.total,
  };
}

/**
 * Derive zone-test characteristics from published APACE+BACC counts.
 * Kept as a function (not just JSON) so the derivation is auditable in tests.
 * Prevalence is taken as reported (17%, rounded); totals follow from it.
 */
export function deriveCharacteristics({
  n = 4368,
  prevalence = 0.17,
  ruledOut = 2493,
  ruledOutNstemi = 5,
  ruledIn = 768,
  ruledInNstemi = 572,
} = {}) {
  const nstemiTotal = Math.round(n * prevalence);
  const nonNstemiTotal = n - nstemiTotal;
  return {
    nstemiTotal,
    nonNstemiTotal,
    ruleOut: {
      sensitivity: (nstemiTotal - ruledOutNstemi) / nstemiTotal,
      specificity: (ruledOut - ruledOutNstemi) / nonNstemiTotal,
    },
    ruleIn: {
      sensitivity: ruledInNstemi / nstemiTotal,
      specificity:
        (nonNstemiTotal - (ruledIn - ruledInNstemi)) / nonNstemiTotal,
    },
  };
}
